<template>
  <div class="subtitle-overlay" v-if="visible">
    <!-- 字幕文字 -->
    <div
      class="subtitle-text-wrap"
      v-show="subtitleEnabled && currentText"
      :class="{ 'subtitle-text-wrap--fullscreen': isFullScreen, 'subtitle-text-wrap--controls': controlsVisible }"
    >
      <span class="subtitle-text">{{ currentText }}</span>
    </div>
  </div>
</template>

<script>
import { getJSON } from "@saas/service-api/src/request";

const SUBTITLE_PREF_KEY = "subtitle_preference";

export default {
  name: "SubtitleOverlay",
  props: {
    // 字幕JSON文件URL
    subtitleUrl: {
      type: String,
      default: "",
    },
    // 当前播放时间（秒）
    currentTime: {
      type: Number,
      default: 0,
    },
    // 是否全屏
    isFullScreen: {
      type: Boolean,
      default: false,
    },
    // 播放器控制栏是否可见
    controlsVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      subtitleList: [],
      subtitleEnabled: true,
      loading: false,
    };
  },
  computed: {
    // 是否应该显示（有URL即显示组件）
    visible() {
      return !!this.subtitleUrl;
    },
    // 当前应该显示的字幕文本
    currentText() {
      if (!this.subtitleList.length) return "";
      const time = this.currentTime;
      // 二分查找优化性能
      const item = this.findSubtitleAtTime(time);
      return item ? item.text : "";
    },
  },
  watch: {
    subtitleUrl: {
      handler(url) {
        if (url) {
          this.fetchSubtitles(url);
        } else {
          this.subtitleList = [];
        }
      },
      immediate: true,
    },
  },
  created() {
    // 恢复用户偏好
    const pref = localStorage.getItem(SUBTITLE_PREF_KEY);
    if (pref !== null) {
      this.subtitleEnabled = pref === "1";
    }
  },
  mounted() {
    // 通知父组件当前字幕开关的真实状态，确保 CC 按钮颜色同步
    this.$emit("subtitleToggle", this.subtitleEnabled);
  },
  methods: {
    async fetchSubtitles(url) {
      if (!url || url === this._lastFetchedUrl) return;
      // 记录当前请求的 URL，用于防止旧请求覆盖新数据
      this._lastFetchedUrl = url;
      this.loading = true;
      try {
        const data = await getJSON(url);
        // 防止旧请求的结果覆盖：如果请求期间 URL 又变了，丢弃本次结果
        if (this._lastFetchedUrl !== url) return;
        if (!Array.isArray(data)) {
          this.subtitleList = [];
          return;
        }
        // 将所有 item 的 words 扁平化并按时间排序
        const allWords = [];
        data.forEach(item => {
          if (Array.isArray(item.words) && item.words.length > 0) {
            item.words.forEach(w => allWords.push(w));
          }
        });
        allWords.sort((a, b) => a.start_ms - b.start_ms);
        // 按"自然停顿 + 字数上限"分组为短句字幕
        this.subtitleList = this._groupWordsToLines(allWords);
      } catch (err) {
        console.warn("[SubtitleOverlay] Subtitle load error:", err);
        if (this._lastFetchedUrl === url) {
          this._lastFetchedUrl = '';
          this.subtitleList = [];
        }
      } finally {
        this.loading = false;
      }
    },
    // 二分查找当前时间对应的字幕
    findSubtitleAtTime(time) {
      const list = this.subtitleList;
      if (!list.length) return null;

      let low = 0;
      let high = list.length - 1;
      let result = null;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const item = list[mid];
        if (time >= item.start_time && time <= item.end_time) {
          return item;
        } else if (time < item.start_time) {
          high = mid - 1;
        } else {
          low = mid + 1;
        }
      }
      return result;
    },
    /**
     * 将词级别数据分组为短句字幕行
     * 多层级断句策略：语气词 + 停顿 + 弹性字数
     */
    _groupWordsToLines(words) {
      if (!words.length) return [];
      // 语气词/句末助词集合 —— 这些词后面是自然断句点
      const PARTICLES = new Set([
        '啊', '呢', '吧', '嘛', '吗', '哈', '呀', '了', '的',
        '哦', '噢', '喽', '嘞', '呐', '哇', '么', '嗯',
      ]);
      const HARD_MAX = 18;   // 硬上限：超过必断
      const SOFT_MAX = 12;   // 软上限：有机会就断
      const MIN_CHARS = 6;   // 最少积累字数才考虑断句
      const lines = [];
      let lineWords = [words[0]];

      const pushLine = (endWord) => {
        const text = lineWords.map(w => w.word).join('');
        if (text.length > 0) {
          lines.push({
            start_time: lineWords[0].start_ms / 1000,
            end_time: endWord.end_ms / 1000,
            text,
          });
        }
      };

      for (let i = 1; i < words.length; i++) {
        const prev = words[i - 1];
        const curr = words[i];
        const gap = curr.start_ms - prev.end_ms;
        const charCount = lineWords.reduce((n, w) => n + w.word.length, 0);
        const prevWord = prev.word;
        // 最后一个字符是否为语气词
        const endsWithParticle = PARTICLES.has(prevWord) ||
          (prevWord.length > 1 && PARTICLES.has(prevWord[prevWord.length - 1]));
        let shouldBreak = false;

        // ====== 停顿优先断句 ======
        // 层级1：明显停顿(≥200ms) → 直接断句（最高优先级）
        if (gap >= 200) {
          shouldBreak = true;
        }
        // 层级2：短停顿(≥100ms) + 已有最少字数 → 断
        else if (gap >= 100 && charCount >= 4) {
          shouldBreak = true;
        }
        // 层级3：语气词结尾 + 微停顿(≥50ms) + 有足够字数 → 断
        else if (endsWithParticle && gap >= 50 && charCount >= MIN_CHARS) {
          shouldBreak = true;
        }
        // 层级4：硬字数上限 → 安全兜底
        else if (charCount >= HARD_MAX) {
          shouldBreak = true;
        }

        if (shouldBreak) {
          pushLine(prev);
          lineWords = [curr];
        } else {
          lineWords.push(curr);
        }
      }
      // 最后一组
      if (lineWords.length > 0) {
        pushLine(lineWords[lineWords.length - 1]);
      }
      return lines;
    },
    toggleSubtitle() {
      this.subtitleEnabled = !this.subtitleEnabled;
      localStorage.setItem(SUBTITLE_PREF_KEY, this.subtitleEnabled ? "1" : "0");
      this.$emit("subtitleToggle", this.subtitleEnabled);
    },
  },
};
</script>

<style scoped lang="scss">
.subtitle-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.subtitle-text-wrap {
  position: absolute;
  bottom: 40px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 100;
  text-align: center;
  pointer-events: none;
  transition: bottom 0.3s ease;

  &--controls {
    bottom: 80px;
  }

  &--fullscreen {
    bottom: 80px;
    &.subtitle-text-wrap--controls {
      bottom: 120px;
    }
    .subtitle-text {
      font-size: 20px;
      padding: 8px 16px;
    }
  }
}

.subtitle-text {
  display: inline-block;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 16px;
  line-height: 1.5;
  padding: 6px 12px;
  border-radius: 4px;
  word-break: break-word;
  max-width: 90%;
}

// 移动端适配
@media (max-width: 768px) {
  .subtitle-text-wrap {
    bottom: 20px;

    &--controls {
      bottom: 50px;
    }
  }

  .subtitle-text {
    font-size: 12px;
    padding: 3px 8px;
    line-height: 1.4;
    max-width: 96%;
  }
}
</style>
